/**
 * SubscribeGate — what a non-subscriber sees instead of the app.
 *
 * The server refused the launch with `403 NOT_SUBSCRIBED`, so there is no session: no
 * catalogue, no queue, no player. This screen **replaces** the shell's contents rather than
 * sitting over them, and it carries exactly two controls:
 *
 *  1. **«Открыть канал»** — the only thing that can change the situation. Rendered only when
 *     the server sent a link: a button that opens nothing is worse than no button, so a
 *     deployment with no `TG_CHANNEL_JOIN_URL` gets the sentence alone.
 *  2. **«Я подписался»** — re-runs the login. The server never caches a refusal, so this
 *     answers truthfully the moment the person has joined. **No page reload** — a reload
 *     throws away the launch data.
 *
 * ## This is not an error screen
 *
 * Nothing is broken: the app is working exactly as configured. The copy rule is the one
 * every screen follows — say what happened and what to do, never apologise, never blame.
 *
 * ## The neon sign (task 68)
 *
 * The flag along the top edge, the sign «Только для подписчиков» with its padlock and the
 * chevrons running down to the two buttons — the screen's title, for a screen reader too — the
 * sentence, and the buttons at the bottom, under the thumb, 60 points tall.
 */

import type { JSX } from 'react';
import { useState } from 'react';

import { NeonSign } from '../components/NeonSign';
import { telegram } from '../lib/telegram';

/** The sentence. Sentence case: tracked Cyrillic caps read as shouting. Task 68: the channel named. */
export const GATE_SENTENCE = 'Плеер открыт только для подписчиков канала Flash Dancehall.';
/** The sign's words, the screen's title (`components/NeonSign.tsx`). */
export const GATE_TITLE = 'Только для подписчиков';
export const OPEN_CHANNEL_LABEL = 'Открыть канал';
export const SUBSCRIBED_LABEL = 'Я подписался';
export const CHECKING_LABEL = 'Проверяю…';

export interface SubscribeGateProps {
  /** From `error.details.joinUrl`. Absent means no button. */
  readonly joinUrl: string | undefined;
  /** Re-runs the launch. */
  readonly onRetry: () => void | Promise<void>;
}

export function SubscribeGate({ joinUrl, onRetry }: SubscribeGateProps): JSX.Element {
  const [checking, setChecking] = useState(false);

  const retry = (): void => {
    setChecking(true);
    void Promise.resolve(onRetry()).finally(() => {
      setChecking(false);
    });
  };

  return (
    <section className="gate">
      <div className="flagStripe" aria-hidden="true" />
      <div className="gateBody">
        <NeonSign kind="podpiska" level={1} />
        <p className="gateText">{GATE_SENTENCE}</p>
      </div>
      <div className="gateActions">
        {joinUrl !== undefined && (
          <button
            type="button"
            className="button gateButton"
            onClick={() => {
              // Through the wrapper, never `window.open`: inside Telegram a `t.me` link has
              // to be opened by the client, or it lands in a browser outside the app.
              telegram.openTelegramLink(joinUrl);
            }}
          >
            {OPEN_CHANNEL_LABEL}
          </button>
        )}
        <button
          type="button"
          className="button buttonSecondary gateButton"
          onClick={retry}
          disabled={checking}
        >
          {checking ? CHECKING_LABEL : SUBSCRIBED_LABEL}
        </button>
      </div>
    </section>
  );
}
